import { useState, useEffect } from 'react';
import { Textarea } from "./components/ui/textarea";
import { Button } from "./components/ui/button";
import LeakerInput from "./components/LeakerInput";
import LeakerCard from "./components/LeakerCard";
import ArticleMatchCard from "./components/ArticleMatchCard";
import ForceGraph from "./components/ForceGraph";
import ReactMarkdown from 'react-markdown';
import LeakAnalysisVisualizations from './components/LeakAnalysisVisualizations';
import MonitoringPage from './components/MonitoringPage';
import ResearchPage from './components/ResearchPage';
import SystemLogsPage from './components/SystemLogsPage';

export default function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [inputPassword, setInputPassword] = useState('');
  const [queryText, setQueryText] = useState('');
  const [results, setResults] = useState([]);
  const [leakers, setLeakers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leakSummary, setLeakSummary] = useState("");
  const HARDCODED_PASSWORD = "nomoredetect2025";
  const [filterJournalist, setFilterJournalist] = useState('');
  const [filterPublication, setFilterPublication] = useState('');
  const [showOnlyFlagged, setShowOnlyFlagged] = useState(false);
  const [preLeakDate, setPreLeakDate] = useState("2017-06-05");
  const [usePreLeakCutoff, setUsePreLeakCutoff] = useState(true);
  const [modelSelection, setModelSelection] = useState("default");
  const [currentView, setCurrentView] = useState("analysis");
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [saveTitle, setSaveTitle] = useState("");
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hidden Logs Page State
  const [logsAuthenticated, setLogsAuthenticated] = useState(false);
  const [logPassword, setLogPassword] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutTime, setLockoutTime] = useState(0);
  const LOG_PASSWORD = "nomoreadmin-logs-2026"; // Dedicated password for logs

  const API_BASE = "https://kiss-node-orchestrator-1057209991970.us-central1.run.app";

  // Lockout timer logic
  useEffect(() => {
    if (lockoutTime > 0) {
      const timer = setInterval(() => setLockoutTime(prev => prev - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [lockoutTime]);

  const handleLogLogin = () => {
    if (lockoutTime > 0) return;
    
    if (logPassword === LOG_PASSWORD) {
      setLogsAuthenticated(true);
      setFailedAttempts(0);
    } else {
      const nextAttempts = failedAttempts + 1;
      setFailedAttempts(nextAttempts);
      // Progressive lockout: 5s, 15s, 30s, 60s
      const wait = nextAttempts >= 4 ? 60 : nextAttempts * 5 + (nextAttempts > 1 ? 5 : 0);
      setLockoutTime(wait);
      setLogPassword('');
    }
  };

  const isLogsRoute = window.location.hash === '#/system-logs';

  const handleLogin = () => {
    if (inputPassword === HARDCODED_PASSWORD) setAuthenticated(true);
    else alert("Incorrect password.");
  };

  const handleSubmit = async () => {
    if (!queryText.trim()) return;
    setLoading(true);
    setLeakSummary("");
    try {
      const res = await fetch("https://kiss-node-orchestrator-1057209991970.us-central1.run.app/matchAgainstGdelt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
                             text: queryText,
                             leakers,
                             filterJournalist,
                             filterPublication,
                             modelSelection,
                             ...(usePreLeakCutoff && { beforeDate: preLeakDate })
                             })
      });

      const data = await res.json();
      const matches = data.matches || [];
      setResults(matches);

      // Construct profileText string from leaker cards
      const profileText = leakers.map(
        (l) => `${l.name}: ${l.note}`
      ).join(" ");

      // Request summary if matches were found
      if (matches.length > 0) {
        const summaryRes = await fetch("https://kiss-node-orchestrator-1057209991970.us-central1.run.app/summarizeLeakMatches", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ matches, profileText })
        });
        const { summary, recommendation } = await summaryRes.json();
        setLeakSummary(`${summary}\n\n**Recommended Next Steps:**\n${recommendation}`);

      }

    } catch (err) {
      console.error("❌ Match fetch error:", err);
      alert("Error fetching matches.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddLeaker = (leaker) => {
    setLeakers(prev => [...prev, leaker]);
  };

  const handleSaveAnalysis = async () => {
    if (!saveTitle.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/saveAnalysis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: saveTitle,
          input_text: queryText,
          leaker_profiles: leakers.length > 0 ? leakers : null,
          model_type: modelSelection,
          analysis_summary: leakSummary
        })
      });
      const data = await res.json();
      if (data.success) {
        setShowSaveInput(false);
        setSaveTitle("");
        alert("Analysis saved!");
      } else {
        alert("Failed to save: " + (data.error || "Unknown error"));
      }
    } catch (err) {
      console.error("Save error:", err);
      alert("Error saving analysis.");
    } finally {
      setSaving(false);
    }
  };

  const fetchSavedAnalyses = async () => {
    try {
      const res = await fetch(`${API_BASE}/savedAnalyses`);
      const data = await res.json();
      setSavedAnalyses(data.analyses || []);
    } catch (err) {
      console.error("Error fetching saved analyses:", err);
    }
  };

  const fetchAnalysisDetail = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/savedAnalysis?id=${id}`);
      const data = await res.json();
      setSelectedAnalysis(data);
    } catch (err) {
      console.error("Error fetching analysis detail:", err);
    }
  };

  useEffect(() => {
    if (currentView === "saved" && authenticated) {
      fetchSavedAnalyses();
    }
  }, [currentView, authenticated]);

  if (isLogsRoute) {
    if (!logsAuthenticated) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-black font-mono">
          <div className="w-full max-w-md p-8 border border-gray-700 bg-gray-900 rounded shadow-2xl">
            <h1 className="text-xl mb-6 text-red-500 font-bold uppercase tracking-widest border-b border-red-900 pb-2">
              Unauthorized Access — System Logs
            </h1>
            <p className="text-xs text-gray-500 mb-6 leading-relaxed">
              You are attempting to access restricted diagnostic data. 
              Please enter the administrative bypass code.
            </p>
            <input
              type="password"
              placeholder="ENTER LOG ACCESS CODE"
              className="w-full p-3 border border-gray-700 rounded mb-4 bg-black text-green-500 font-mono placeholder-gray-800 outline-none focus:border-green-900 transition-colors"
              value={logPassword}
              onChange={(e) => setLogPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogLogin()}
              disabled={lockoutTime > 0}
            />
            <Button 
              className="w-full bg-gray-800 hover:bg-gray-700 text-gray-300"
              onClick={handleLogLogin}
              disabled={lockoutTime > 0}
            >
              {lockoutTime > 0 ? `SYSTEM LOCKED: ${lockoutTime}s` : "AUTHENTICATE"}
            </Button>
            {failedAttempts > 0 && lockoutTime === 0 && (
              <p className="mt-4 text-[10px] text-red-900 text-center uppercase font-bold">
                Invalid Attempt detected. {4 - failedAttempts} attempts remaining before temporary lockout.
              </p>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-black p-8 font-mono">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8 border-b border-gray-800 pb-4">
            <h1 className="text-2xl font-bold text-green-400 uppercase tracking-widest">KISS Intelligence — Audit Log</h1>
            <Button variant="outline" size="sm" onClick={() => window.location.hash = ''}>EXIT TO MAIN</Button>
          </div>
          <SystemLogsPage />
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900">
        <h1 className="text-2xl mb-4 text-green-400 font-mono">Login</h1>
        <input
          type="password"
          placeholder="Enter password"
          className="p-2 border border-gray-600 rounded mb-2 bg-gray-800 text-green-400 font-mono placeholder-gray-500"
          value={inputPassword}
          onChange={(e) => setInputPassword(e.target.value)}
        />
        <Button onClick={handleLogin}>Submit</Button>
      </div>
    );
  }

  

  return (
    <div className="min-h-screen bg-black">
      <div className="p-6 max-w-4xl mx-auto font-mono">
        <h1 className="text-3xl font-bold mb-4 text-green-400">KISS Leak Analysis Tool</h1>

      {/* Nav Toggle */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={currentView === "analysis" ? "default" : "outline"}
          onClick={() => { setCurrentView("analysis"); setSelectedAnalysis(null); }}
        >
          Analysis
        </Button>
        <Button
          variant={currentView === "saved" ? "default" : "outline"}
          onClick={() => setCurrentView("saved")}
        >
          Saved Analyses
        </Button>
        <Button
          variant={currentView === "monitoring" ? "default" : "outline"}
          onClick={() => setCurrentView("monitoring")}
        >
          Monitoring
        </Button>
        <Button
          variant={currentView === "research" ? "default" : "outline"}
          onClick={() => setCurrentView("research")}
        >
          Research
        </Button>
      </div>

      {currentView === "analysis" && (
      <>
      {/* Demo Buttons */}
      <div className="flex flex-wrap gap-2 mb-4 text-green-400">
        <Button
          variant="outline"
          onClick={() =>
            setQueryText(
              "This classified report outlines how Russian military intelligence (GRU) launched spear-phishing campaigns against U.S. local election officials just days before the 2016 election. It confirms malware deployment efforts targeting VR Systems and states that at least one U.S. company providing election support software was compromised. The analysis indicates persistent access and reconnaissance by Russian-affiliated operatives across multiple jurisdictions."
            )
          }
        >
          Load Example Input
        </Button>

        <Button
          variant="outline"
          onClick={() =>
            setLeakers([
              {
                "name": "Tess Halbrook",
                "occupation": "NSA Contract Linguist",
                "motivation": "Concern about election integrity",
                "access": "SIGINT reports and classified assessments",
                "notes": "Frustrated by inaction within the agency regarding foreign influence."
              },
              {
                "name": "Kyle Ramirez",
                "occupation": "Military Intelligence Analyst",
                "motivation": "Moral disagreement with classified actions abroad",
                "access": "Daily intelligence briefs and intercepts",
                "notes": "Previously reported security concerns internally."
              },
              {
                "name": "Lena Cho",
                "occupation": "Cybersecurity Contractor",
                "motivation": "Desire to expose vulnerabilities",
                "access": "Election system breach assessments",
                "notes": "Worked on post-election audit team."
              }
            ])
          }
        >
          Add Demo Leakers
        </Button>
      </div>

      {/* Leaker Input and Cards */}
      <LeakerInput onAddLeaker={handleAddLeaker} />
      {leakers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {leakers.map((leaker, i) => (
            <LeakerCard key={i} leaker={leaker} />
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4 text-green-400">
        <input
          type="text"
          placeholder="Filter by journalist name (optional)"
          className="p-2 border border-gray-600 rounded bg-gray-800 text-green-400 placeholder-gray-500"
          value={filterJournalist}
          onChange={(e) => setFilterJournalist(e.target.value)}
        />
        <input
          type="text"
          placeholder="Filter by publication name (optional)"
          className="p-2 border border-gray-600 rounded bg-gray-800 text-green-400 placeholder-gray-500"
          value={filterPublication}
          onChange={(e) => setFilterPublication(e.target.value)}
        />

        <label className="flex items-center gap-2 text-sm mt-2">
          <input
            type="checkbox"
            checked={showOnlyFlagged}
            onChange={(e) => setShowOnlyFlagged(e.target.checked)}
            className="accent-green-400"
          />
          Show only articles with leak flags
        </label>

        <div className="flex items-center gap-3 text-sm mt-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={usePreLeakCutoff}
              onChange={(e) => setUsePreLeakCutoff(e.target.checked)}
              className="accent-green-400"
            />
            Pre-Leak Cutoff
          </label>
          <input
            type="date"
            value={preLeakDate}
            onChange={(e) => setPreLeakDate(e.target.value)}
            className="border border-gray-600 px-2 py-1 rounded text-sm bg-gray-800 text-green-400"
            disabled={!usePreLeakCutoff}
          />
        </div>

      </div>

      {/* Model Selection */}
      <div className="border border-gray-600 rounded p-4 mb-4 bg-gray-900">
        <h3 className="text-lg font-semibold mb-3 text-green-400">KISS Model Selection</h3>
        <div className="flex flex-wrap gap-4 text-green-400">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="modelSelection"
              value="default"
              checked={modelSelection === "default"}
              onChange={(e) => setModelSelection(e.target.value)}
              className="accent-green-400"
            />
            Default Processing
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="modelSelection"
              value="lean"
              checked={modelSelection === "lean"}
              onChange={(e) => setModelSelection(e.target.value)}
              className="accent-green-400"
            />
            Lean Analysis
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="modelSelection"
              value="grok"
              checked={modelSelection === "grok"}
              onChange={(e) => setModelSelection(e.target.value)}
              className="accent-green-400"
            />
            Grok Analysis
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="modelSelection"
              value="deep"
              checked={modelSelection === "deep"}
              onChange={(e) => setModelSelection(e.target.value)}
              className="accent-green-400"
            />
            Deep Analysis
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="modelSelection"
              value="experimental"
              checked={modelSelection === "experimental"}
              onChange={(e) => setModelSelection(e.target.value)}
              className="accent-green-400"
            />
            Experimental Analysis
          </label>
        </div>
      </div>

      {/* Input Form */}
      <Textarea
        rows={6}
        placeholder="Paste leaked content or describe the incident (e.g. 'An internal memo predicted a 1.5% GDP drop. The article quoted phrases from that draft.')"
        className="mb-4 bg-gray-800 border-gray-600 text-green-400 placeholder-gray-500 font-mono"
        value={queryText}
        onChange={(e) => setQueryText(e.target.value)}
      />
      <Button onClick={handleSubmit} disabled={loading}>
        {loading ? "Analyzing..." : "Find Matches"}
      </Button>

      {/* Match Results */}
      {leakSummary && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4 text-sm text-blue-900 shadow">
          <h4 className="font-semibold mb-1">KISS Leak Analysis Summary</h4>
          <ReactMarkdown className="prose prose-sm max-w-none text-blue-900">
            {leakSummary}
          </ReactMarkdown>
          <div className="mt-3 border-t border-blue-200 pt-3">
            {!showSaveInput ? (
              <Button variant="outline" onClick={() => setShowSaveInput(true)}>
                Save Analysis
              </Button>
            ) : (
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="Title for this analysis"
                  className="p-2 border border-gray-400 rounded text-sm flex-1"
                  value={saveTitle}
                  onChange={(e) => setSaveTitle(e.target.value)}
                />
                <Button onClick={handleSaveAnalysis} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button variant="outline" onClick={() => { setShowSaveInput(false); setSaveTitle(""); }}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {results.length > 0 ? (
          results
            .filter((m) =>
              showOnlyFlagged ? m.leak_flags && m.leak_flags.length > 0 : true
            )
            .map((match, i) => (
              <ArticleMatchCard
                key={i}
                match={match}
                preLeakDate={preLeakDate}
              />
            ))
        ) : (
          !loading && (
            <div className="text-center text-sm text-green-400 italic">
              No strong matches found. Try rephrasing your input or using terms related to internal decisions, meetings, or policies.
            </div>
          )
        )}
      </div>

      {/* Advanced Leak Analysis Visualizations */}
      <LeakAnalysisVisualizations
        results={results}
        leakers={leakers}
        preLeakDate={preLeakDate}
      />

      {/* Network Graph */}
      {results.length > 0 && (
        <ForceGraph leakers={leakers} results={results} />
      )}
      </>
      )}

      {/* Monitoring View */}
      {currentView === "monitoring" && <MonitoringPage />}

      {/* Research View */}
      {currentView === "research" && <ResearchPage />}

      {/* System Logs View */}
      {currentView === "logs" && <SystemLogsPage />}

      {/* Saved Analyses View */}
      {currentView === "saved" && (
        <div className="space-y-4">
          {selectedAnalysis ? (
            <div className="border border-gray-600 rounded p-4 bg-gray-900 text-green-400">
              <Button variant="outline" onClick={() => setSelectedAnalysis(null)} className="mb-4">
                Back to List
              </Button>
              <h2 className="text-xl font-bold mb-2">{selectedAnalysis.title}</h2>
              <p className="text-xs text-gray-400 mb-4">
                {new Date(selectedAnalysis.created_at).toLocaleString()} | Model: {selectedAnalysis.model_type || "N/A"}
              </p>
              {selectedAnalysis.input_text && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold mb-1 text-gray-300">Input Text</h3>
                  <p className="text-sm whitespace-pre-wrap bg-gray-800 p-3 rounded">{selectedAnalysis.input_text}</p>
                </div>
              )}
              {selectedAnalysis.leaker_profiles && selectedAnalysis.leaker_profiles.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold mb-1 text-gray-300">Leaker Profiles</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedAnalysis.leaker_profiles.map((l, i) => (
                      <div key={i} className="bg-gray-800 p-2 rounded text-xs">
                        <span className="font-bold">{l.name}</span> — {l.occupation || l.motivation || ""}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedAnalysis.analysis_summary && (
                <div>
                  <h3 className="text-sm font-semibold mb-1 text-gray-300">Analysis Summary</h3>
                  <div className="bg-gray-800 p-3 rounded">
                    <ReactMarkdown className="prose prose-sm prose-invert max-w-none text-green-300">
                      {selectedAnalysis.analysis_summary}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-green-400">Saved Analyses</h2>
              {savedAnalyses.length === 0 ? (
                <p className="text-gray-400 text-sm italic">No saved analyses yet.</p>
              ) : (
                savedAnalyses.map((a) => (
                  <div
                    key={a.id}
                    className="border border-gray-600 rounded p-3 bg-gray-900 text-green-400 cursor-pointer hover:border-green-400 transition-colors"
                    onClick={() => fetchAnalysisDetail(a.id)}
                  >
                    <h3 className="font-semibold">{a.title}</h3>
                    <p className="text-xs text-gray-400">
                      {new Date(a.created_at).toLocaleString()} | Model: {a.model_type || "N/A"}
                    </p>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}
      </div>
    </div>
  );
}